import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Sticky bottom-of-screen mobile CTA bar.
 *
 * Behavior:
 *  - Renders only on mobile (< md breakpoint).
 *  - Hidden on dashboard / report / account routes (user is already inside the app).
 *  - Hidden on /upload, /checkout/* (already in a CTA-heavy flow).
 *  - Appears after 200px of scroll to avoid covering the hero CTAs on first view.
 *  - Dismissible via X button — flag stored in sessionStorage (one-tap-to-hide
 *    for the rest of the session). Reappears on the next session.
 *
 * No API calls. No AI. Pure frontend state.
 */
const HIDE_ON_PATHS = [
    "/dashboard",
    "/account",
    "/report",
    "/admin",
    "/upload",
    "/checkout",
    "/auth",
    "/forgot-password",
    "/reset-password",
    "/verify-email",
    "/recover-account",
    "/support",
];

const DISMISS_KEY = "bl-mobile-cta-dismissed";

export default function StickyMobileCTA() {
    const { pathname } = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        try {
            setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
        } catch { /* non-critical */ }
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 200);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const shouldHide =
        dismissed ||
        !scrolled ||
        HIDE_ON_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));

    if (shouldHide) return null;

    const handleDismiss = () => {
        try {
            sessionStorage.setItem(DISMISS_KEY, "1");
        } catch { /* non-critical */ }
        setDismissed(true);
    };

    return (
        <div
            className="md:hidden fixed inset-x-0 bottom-0 z-40 px-3 pb-3 pointer-events-none"
            data-testid="sticky-mobile-cta"
        >
            <div className="bl-card border border-bl shadow-lg pointer-events-auto flex items-center gap-2 px-3 py-2.5 bg-bl-paper">
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-bl-primary">
                        From $49 · One-time
                    </div>
                    <div className="text-[13px] text-bl font-medium leading-tight truncate">
                        Turn your inspection into a clear plan.
                    </div>
                </div>
                <Link
                    to="/upload"
                    className="bl-btn-primary text-sm py-2 px-3 flex-shrink-0"
                    data-testid="sticky-mobile-cta-upload"
                >
                    Upload <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
                <button
                    type="button"
                    onClick={handleDismiss}
                    className="text-bl-muted hover:text-bl text-lg leading-none px-1.5"
                    aria-label="Dismiss"
                    data-testid="sticky-mobile-cta-dismiss"
                >
                    ×
                </button>
            </div>
        </div>
    );
}
